import { merge, race } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { filter, map, takeUntil, last, share } from 'rxjs/operators'
import { distinctCount } from 'utils/rx-util'
import { MessageStatus, MessageDeliveryStatus } from './MessageStatus'

/**
 * Stream for message status deltas.
 *
 * @param statusChanges$ the status change stream for the message, belonging to a single user
 * @param messageRemoved$ a stream that emits a value when the message is removed
 */
export function MessageStatusDeltaStream(
  statusChanges$: Observable<MessageStatus>,
  messageRemoved$: Observable<unknown>,
) {
  // Since we subscribe twice to this one, make sure it's a shared observable.
  const sharedRemove$ = messageRemoved$.pipe(share())

  // Observables for read and delivered statuses.
  // If the same event comes in for the same status, ignore it.
  const delivered$ = statusChanges$.pipe(
    filter((s) => s.deliveryStatus === MessageDeliveryStatus.Delivered),
    distinctCount(100, (s) => s.id),
  )
  const read$ = statusChanges$.pipe(
    filter((s) => s.deliveryStatus === MessageDeliveryStatus.Read),
    distinctCount(100, (s) => s.id),
  )

  // Sync the unread count.
  return merge(
    // Map delivered and reads to deltas.
    delivered$.pipe(map(() => 1)),
    // So this is fun stuff.
    // When we get a delivered, and then the message gets deleted,
    // if we haven't emitted a corresponding read, decrement again.
    race(read$.pipe(map(() => -1)), sharedRemove$.pipe(map(() => -1))),
  ).pipe(
    // Unsubscribe once the message have been removed, but
    // only after the observable completes.
    takeUntil(sharedRemove$.pipe(last())),
  )
}
